import { z } from 'zod';
import { getStoresProductSchema } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/store_product.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import {
  formKeyMap,
  keyNameFormPathMap,
  ValidateField,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { CreatorProduct } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/types.ts';
import { ValueAtPath } from '../../../../../../shared/type/ObjectPaths.ts';
import { extractFieldSchema } from '../../../../../../shared/zod-helper/extractFieldSchema.ts';
import { every, isNil, isNumber, pipe, some } from '@fxts/core';
import { StoreProductQnaType } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/StoreProduct/StoreProduct.ts';
import { CategoryLevel2_CODE } from '@shared/mps-shared/domain/category/types.ts';
import { PriceS } from '@modules/Price/S/Function/module/PriceS';
import { $ } from 'rune-ts';

export enum ValidateFormErrorKeyTypes {
  EMPTY_THUMBNAIL = 'EMPTY_THUMBNAIL',
  MORE_THAN_SELL_END_AT = 'MORE_THAN_SELL_END_AT',
  PUBLIC_SKU = 'PUBLIC_SKU',
  ALL_CLEAR_SKU_PUBLIC_SKU = 'ALL_CLEAR_SKU_PUBLIC_SKU',
  NOT_PUBLIC_SKU = 'NOT_PUBLIC_SKU',
  NOT_ALL_CLEAR_SKU = 'NOT_ALL_CLEAR_SKU',
  CANCELLED_SKU = 'CANCELLED_SKU',
  CLOSE_MODAL = 'CLOSE_MODAL',
  DUPLICATED_NICKNAME = 'DUPLICATED_NICKNAME',
  NOT_KC_CERTIFICATION = 'NOT_KC_CERTIFICATION',
  NOT_QNA_SNS_LINK = 'NOT_QNA_SNS_LINK',
  NOT_MATERIAL = 'NOT_MATERIAL',
  NOT_ALL_CLEAR_SKU_VOLUME = 'NOT_ALL_CLEAR_SKU_VOLUME',
  MORE_THAN_GOAL_AMOUNT = 'MORE_THAN_GOAL_AMOUNT',
  NOT_GOAL_AMOUNT = 'NOT_GOAL_AMOUNT',
  MIN_MARKETING_PRICE = 'MIN_MARKETING_PRICE',
}

export const validateSingleFieldByKey = <
  M extends MODE,
  K extends ValidateField,
  ActualPath extends (typeof keyNameFormPathMap)[K], // 경로의 실제 타입
>(
  mode: M,
  keyName: K,
  // value의 타입을 CreatorProduct<M> (현재 모드) 기준으로 변경
  value: ValueAtPath<CreatorProduct<M>, ActualPath>,
): string | null => {
  const fullSchemaForMode = getStoresProductSchema(mode);
  const pathValue = keyNameFormPathMap[keyName];

  try {
    // fullSchemaForMode (CreatorProduct<M> 스키마)에서 경로에 해당하는 필드 스키마 추출
    const fieldSchema = extractFieldSchema(fullSchemaForMode, pathValue);
    if (!fieldSchema) {
      return `필드 스키마를 찾을 수 없습니다 (경로: ${pathValue}).`;
    }
    // 추출된 필드 스키마로 값(CreatorProduct<M> 기준 타입) 유효성 검사
    fieldSchema.parse(value);
    return null; // 유효성 검사 통과
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message ?? '유효성 검사 오류';
    }
    console.error('Unexpected validation error:', error);
    return '알 수 없는 유효성 검사 오류입니다.';
  }
};

export const validateNewFormData = (
  formData: CreatorProduct<typeof MODE.NEW>,
): Array<ValidateFormErrorKeyTypes> => {
  const error_keys: Array<ValidateFormErrorKeyTypes> = [];

  // sell_start_dat와 sell_end_date가 존재하고 sell_start_dat러보다 값이 작다면 에러
  if (formData.sell_start_at && formData.sell_end_at && formData.sell_end_at <= formData.sell_start_at)
    error_keys.push(ValidateFormErrorKeyTypes.MORE_THAN_SELL_END_AT);

  return error_keys;
};

export const validateEditFormData = (
  formData: CreatorProduct<typeof MODE.NEW>,
): Array<ValidateFormErrorKeyTypes> => {
  return validateNewFormData(formData);
};

export const validateEditTplFormData = (
  formData: CreatorProduct<typeof MODE.NEW>,
): Array<ValidateFormErrorKeyTypes> => {
  return validateEditFormData(formData);
};

export const validateEditTplIntlFormData = (
  formData: CreatorProduct<typeof MODE.EDIT_TPL_INTL>,
): Array<ValidateFormErrorKeyTypes> => {
  const error_keys: Array<ValidateFormErrorKeyTypes> = validateEditTplFormData(formData);

  // 체적 정보가 모두 입력되어 있지 않다면 에러
  if (
    formData._.product?._?.base_product?.is_use_creator_en &&
    !pipe(
      formData._.product._.spo._.spo_items,
      every((spo_item) => {
        const sku = spo_item?._?.sku;
        return Boolean(sku && sku.width && sku.height && sku.length);
      }),
    )
  )
    error_keys.push(ValidateFormErrorKeyTypes.NOT_ALL_CLEAR_SKU_VOLUME);

  return error_keys;
};

/**
 * @description SKU 관련 조건 에러 핸들링을 처리합니다.
 */
export const validationSkuFormData = (
  formData: CreatorProduct<typeof MODE.NEW>,
): Array<ValidateFormErrorKeyTypes> => {
  const error_keys: Array<ValidateFormErrorKeyTypes> = validateEditTplFormData(formData);

  const is_all_sku = pipe(
    formData._.product._.spo._.spo_items,
    every((spo_item) => {
      return Boolean(spo_item?._?.sku?.nickname);
    }),
  );
  const is_not_all_sku = pipe(
    formData._.product._.spo._.spo_items,
    every((spo_item) => {
      return !spo_item?._?.sku?.nickname;
    }),
  );

  // sku 중복이 존재한다면 에러
  if (
    pipe(new Set(), (set) =>
      formData._.product._.spo._.spo_items.some((spo_item) => {
        const has_sku = set.has(spo_item._.sku?.nickname);
        if (!has_sku && !isNil(spo_item._.sku?.nickname)) set.add(spo_item._.sku?.nickname);
        return has_sku;
      }),
    )
  )
    error_keys.push(ValidateFormErrorKeyTypes.DUPLICATED_NICKNAME);

  // 공개상품 또는 해외배송 상품인 경우 sku가 모두 채워져 있지 않다면 에러
  if (
    (formData._.product.is_public || formData._.product?._?.base_product?.is_use_creator_en) &&
    is_not_all_sku
  )
    error_keys.push(ValidateFormErrorKeyTypes.PUBLIC_SKU);
  if ((formData._.product.is_public || formData._.product?._?.base_product?.is_use_creator_en) && is_all_sku)
    error_keys.push(ValidateFormErrorKeyTypes.ALL_CLEAR_SKU_PUBLIC_SKU);

  // 공개상품이 아니고 sku가 모두 채워져 있다면 에러
  if (!formData._.product.is_public && is_all_sku) error_keys.push(ValidateFormErrorKeyTypes.NOT_PUBLIC_SKU);

  // sku가 하나라도 입력되어 있을 때 나머지도 모두 입력되어 있지 않다면 에러
  if (
    pipe(
      formData._.product._.spo._.spo_items,
      some((spo_item) => spo_item._.sku && !!spo_item._.sku.nickname),
    ) &&
    !is_all_sku
  )
    error_keys.push(ValidateFormErrorKeyTypes.NOT_ALL_CLEAR_SKU);

  return error_keys;
};

export const validationOptionalFormData = (
  formData: CreatorProduct<typeof MODE.NEW>,
): Array<ValidateFormErrorKeyTypes> => {
  const error_keys: Array<ValidateFormErrorKeyTypes> = validateEditTplFormData(formData);

  // KC 인증 체크했으면서 KC 인증서 입력 안한 경우
  if (formData.is_kc_cert_checked && !formData.certification_info)
    error_keys.push(ValidateFormErrorKeyTypes.NOT_KC_CERTIFICATION);

  // 문의 방법 SNS이면서 SNS 링크 입력을 안한 경우
  if (formData.qna_type === StoreProductQnaType.SNS && !formData.qna_link)
    error_keys.push(ValidateFormErrorKeyTypes.NOT_QNA_SNS_LINK);

  // 카테고리가 의류면서 의류 소재를 입력 안한 경우
  /* TODO: 카테고리 코드 직접 접근 보다는 서비스 사용 고려 */
  if (
    formData._.product?._?.base_product?.is_use_creator_en &&
    CategoryLevel2_CODE.APPAREL === formData.sp_cate_list_code &&
    !formData.material
  )
    error_keys.push(ValidateFormErrorKeyTypes.NOT_MATERIAL);

  /**
   * 펀딩 가격이 소비자 가격보다 낮으면 에러
   */
  if (
    formData?.meta?.funding?.target_amount &&
    formData.meta.funding.target_amount < formData._.product.profit
  )
    error_keys.push(ValidateFormErrorKeyTypes.MORE_THAN_GOAL_AMOUNT);

  /**
   * 펀딩 설명이 입력되어있으면서 목표 금액을 입력 안한 경우
   */
  if (!formData?.meta?.funding?.target_amount && !!formData?.meta?.funding?.description)
    error_keys.push(ValidateFormErrorKeyTypes.NOT_GOAL_AMOUNT);

  /**
   * 마케팅 가격이 상품 가격에서 최소 100원 이상이 아닌 경우
   */
  if (
    $(`[name="${formKeyMap.pre_discount_price}"]`)?.element?.()?.checkVisibility() &&
    isNumber(formData._.product?.pre_discount_product?.pre_discount_price) &&
    PriceS.add(formData._.product.profit, 100) > formData._.product?.pre_discount_product?.pre_discount_price
  )
    error_keys.push(ValidateFormErrorKeyTypes.MIN_MARKETING_PRICE);

  return error_keys;
};
